import { useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { removeTermScheduleRow } from "../admissionApi";

const getApiErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || "";

/**
 * Confirms removing one row of the term schedule and removes it on the server straight away, the
 * same moment it leaves the screen. Waiting for the next save would leave every later row showing
 * the notes and tasks of the row above it in the meantime, since those follow position.
 *
 * `target` is null while closed. `onRemoved(rowId, removedOnServer)` lets the table drop the row;
 * removedOnServer is false for a row that was added on screen and never saved.
 */
export default function RemoveTermRowDialog({ admissionId, target, onClose, onRemoved }) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await removeTermScheduleRow(admissionId, target.rowId);
      const removedOnServer = result?.removed === true;
      if (removedOnServer) toast.success(`Term T${target.termNumber} removed`);
      onRemoved(target.rowId, removedOnServer);
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e) || "Failed to remove the term");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open={Boolean(target)}
      title={target ? `Remove term T${target.termNumber}?` : ""}
      message="The term is removed now, with everything listed below, and the terms after it move up one place. This cannot be undone."
      content={target && <RemovalSummary target={target} />}
      confirmLabel="Remove term"
      confirmColor="error"
      loading={submitting}
      onConfirm={handleConfirm}
      onCancel={onClose}
    />
  );
}

function RemovalSummary({ target }) {
  const rows = [
    ["Discussion notes", target.notesCount],
    ["Tasks", target.tasksCount],
    ["Account notes", target.accountNotesCount],
    ["Later terms moved up", target.laterTermsCount],
  ].filter(([, count]) => count > 0);
  if (rows.length === 0) return null;
  return (
    <div className="rounded border bg-app-surface-muted p-3 text-xs flex flex-col gap-1">
      {rows.map(([label, count]) => (
        <div key={label} className="flex justify-between gap-2">
          <span className="text-app-text-secondary">{label}</span>
          <span className="font-medium">{count}</span>
        </div>
      ))}
    </div>
  );
}
